import React, { useEffect, useState } from "react";
import {
  EuiBadge,
  EuiBadgeGroup,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutHeader,
  EuiHorizontalRule,
  EuiIcon,
  EuiLoadingSpinner,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
  EuiToolTip,
} from "@elastic/eui";
import { AdminUser, useApi } from "../";
import { useAnketaAuth } from "../auth/context";
import { useAnketaI18n } from "src/internationalization";

interface ActionPerm {
  action: string;
  granted: boolean;
  grantingRoles: string[];
}
interface ResourcePerm {
  resource: string;
  actions: ActionPerm[];
}
interface UserPermissions {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizations: string[];
  clientRoles: string[];
  realmRoles: string[];
  flags: { isInternal: boolean; isAutonubil: boolean; isMedocino: boolean };
  sudo: boolean;
  permissions: ResourcePerm[];
}

// Route-level narrows that the matrix grant alone does not capture: some actions
// additionally require an org-attribute flag at the server. Surfacing them here
// keeps the "effective access" view honest (a matrix grant the user's org can't
// satisfy is not real access). Mirrors the route checks (see SECURITY.md §5).
const NARROW: Record<string, Record<string, "isAutonubil" | "isInternal">> = {
  questionnaires: {
    delete: "isAutonubil",
    archive: "isAutonubil",
    create: "isInternal",
  },
  "opsanio/prtg": { create: "isInternal" },
  "opsanio/otobo": { create: "isInternal" },
  "opsanio/pangolin": { create: "isInternal" },
};

/**
 * Read-only flyout showing a single user's effective ABAC permissions: their
 * client roles resolved against the live permission matrix (which role grants
 * each cell), the org-attribute flags that narrow destructive / cross-tenant
 * actions, and a superuser banner when the account bypasses the matrix entirely.
 */
export const UserPermissionsFlyout = ({
  user,
  onClose,
}: {
  user: AdminUser;
  onClose: () => void;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();

  const [data, setData] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      const resp = await Get<any>({
        path: `v2/admin/users/${user.id}/permissions`,
        responseType: "json",
        token: keycloak?.token,
      }).catch((e) => e);
      if (!active) return;
      if (resp?.success) {
        setData((resp?.data as any)?.data ?? null);
      } else {
        const detail = resp?.error?.details ?? resp?.error?.message;
        setError(
          (Array.isArray(detail) ? detail.join(", ") : detail) ||
            translate("anketa.admin.users.perms.error", {
              defaultMessage: "Permissions could not be loaded.",
            }),
        );
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ");

  return (
    <EuiFlyout onClose={onClose} size="m" ownFocus aria-labelledby="userPermsTitle">
      <EuiFlyoutHeader hasBorder>
        <EuiTitle size="s">
          <h2 id="userPermsTitle">
            {translate("anketa.admin.users.perms.title", {
              defaultMessage: "Access & permissions",
            })}
          </h2>
        </EuiTitle>
        <EuiSpacer size="xs" />
        <EuiText size="s" color="subdued">
          {fullName ? `${fullName} · ` : ""}
          {user.email}
        </EuiText>
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        {loading && (
          <EuiFlexGroup alignItems="center" gutterSize="s">
            <EuiFlexItem grow={false}>
              <EuiLoadingSpinner size="m" />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="s">
                {translate("anketa.admin.users.perms.loading", {
                  defaultMessage: "Resolving permissions…",
                })}
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        )}

        {!loading && error && (
          <EuiCallOut color="danger" iconType="alert" title={error} />
        )}

        {!loading && !error && data && (
          <>
            {data.sudo && (
              <>
                <EuiCallOut
                  color="warning"
                  iconType="starFilled"
                  title={translate("anketa.admin.users.perms.sudo", {
                    defaultMessage:
                      "Superuser - bypasses the permission matrix and has full access to every resource.",
                  })}
                />
                <EuiSpacer size="m" />
              </>
            )}

            {/* Roles */}
            <EuiText size="xs">
              <strong>
                {translate("anketa.admin.users.perms.roles", {
                  defaultMessage: "Client roles",
                })}
              </strong>
            </EuiText>
            <EuiSpacer size="xs" />
            {data.clientRoles.length ? (
              <EuiBadgeGroup gutterSize="xs">
                {data.clientRoles.map((r) => (
                  <EuiBadge key={r} color="hollow">
                    {r}
                  </EuiBadge>
                ))}
              </EuiBadgeGroup>
            ) : (
              <EuiText size="s" color="subdued">
                {translate("anketa.admin.users.perms.noRoles", {
                  defaultMessage: "No client roles assigned.",
                })}
              </EuiText>
            )}

            <EuiSpacer size="m" />

            {/* Org-attribute flags that narrow destructive / cross-tenant actions */}
            <EuiText size="xs">
              <strong>
                {translate("anketa.admin.users.perms.flags", {
                  defaultMessage: "Organization attributes",
                })}
              </strong>
            </EuiText>
            <EuiSpacer size="xs" />
            <EuiBadgeGroup gutterSize="xs">
              <EuiBadge color={data.flags.isInternal ? "success" : "default"}>
                isInternal: {String(data.flags.isInternal)}
              </EuiBadge>
              <EuiBadge color={data.flags.isAutonubil ? "success" : "default"}>
                isAutonubil: {String(data.flags.isAutonubil)}
              </EuiBadge>
              <EuiBadge color={data.flags.isMedocino ? "success" : "default"}>
                isMedocino: {String(data.flags.isMedocino)}
              </EuiBadge>
            </EuiBadgeGroup>
            {data.organizations?.length > 0 && (
              <>
                <EuiSpacer size="xs" />
                <EuiText size="xs" color="subdued">
                  {translate("anketa.admin.users.perms.orgs", {
                    values: { orgs: data.organizations.join(", ") },
                    defaultMessage: "Organizations: {{orgs}}",
                  })}
                </EuiText>
              </>
            )}

            <EuiHorizontalRule margin="m" />

            {/* Per-resource effective grants */}
            {data.permissions.map((rp) => (
              <EuiPanel
                key={rp.resource}
                hasShadow={false}
                hasBorder
                paddingSize="s"
                style={{ marginBottom: 8 }}
              >
                <EuiText size="s">
                  <strong>{rp.resource}</strong>
                </EuiText>
                <EuiSpacer size="xs" />
                {rp.actions.map((a) => {
                  const requiredFlag = NARROW[rp.resource]?.[a.action];
                  const flagOk = requiredFlag
                    ? (data.flags as any)[requiredFlag] === true
                    : true;
                  // Effective at the route: matrix-granted AND (no narrow OR flag satisfied).
                  const effective = a.granted && (data.sudo || flagOk);
                  return (
                    <EuiFlexGroup
                      key={a.action}
                      gutterSize="s"
                      alignItems="center"
                      responsive={false}
                    >
                      <EuiFlexItem grow={false}>
                        <EuiIcon
                          type={effective ? "check" : "cross"}
                          color={effective ? "success" : "subdued"}
                        />
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiText size="s">{a.action}</EuiText>
                      </EuiFlexItem>
                      <EuiFlexItem>
                        <EuiBadgeGroup gutterSize="xs">
                          {a.grantingRoles.map((r) => (
                            <EuiBadge key={r} color="hollow">
                              {r}
                            </EuiBadge>
                          ))}
                          {data.sudo && a.grantingRoles.length === 0 && (
                            <EuiBadge color="warning">superuser</EuiBadge>
                          )}
                          {requiredFlag && a.granted && (
                            <EuiToolTip
                              content={translate(
                                "anketa.admin.users.perms.narrow.tip",
                                {
                                  values: { flag: requiredFlag },
                                  defaultMessage:
                                    "Also requires {{flag}} at the server; the user " +
                                    (flagOk ? "satisfies it." : "does NOT satisfy it."),
                                },
                              )}
                            >
                              <EuiBadge color={flagOk ? "success" : "danger"}>
                                {flagOk ? `+${requiredFlag}` : `needs ${requiredFlag}`}
                              </EuiBadge>
                            </EuiToolTip>
                          )}
                        </EuiBadgeGroup>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  );
                })}
              </EuiPanel>
            ))}

            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
              {translate("anketa.admin.users.perms.note", {
                defaultMessage:
                  "✓ = effective at the server (matrix grant, and any required org attribute satisfied). " +
                  "“own vs all” scope (questionnaire membership) is enforced per request and not shown here.",
              })}
            </EuiText>
          </>
        )}
      </EuiFlyoutBody>
    </EuiFlyout>
  );
};
